import { z } from 'zod'
import { type Locale, LocaleSchema } from './locale.ts'

/**
 * The protocol half of a consent record (PRD-27 D-7, ADR-0018, migration `0014`).
 *
 * ── What changed, and why it had to ──
 *
 * The four AGM-042 flows ask a person to read a sentence and then persist that sentence,
 * verbatim, as the durable record of what they agreed to. With one language that was
 * defensible: the sentence was the product's only rendering of the claim, so keeping it kept
 * everything. With two it stops being a record and becomes an artefact of whichever language
 * the person's browser happened to be in — an auditor reading a Portuguese acknowledgement has
 * no way to establish that it said the same thing as the English one filed beside it, and no
 * way to read either without a translator.
 *
 * So the record becomes `{ ackKey, ackVersion, ackLocale, ackParams }` and the rendered text
 * stays as EVIDENCE of what was actually on the screen. Both halves are needed and neither is
 * sufficient: the key and its parameters can be re-rendered in any locale the product ships,
 * and the text is the only thing that says what the person in fact read. A canonical English
 * record alone would be a record of a sentence nobody saw, which is the integrity failure
 * AGM-042 exists to prevent.
 *
 * ── Why the keys are here and the sentences are not ──
 *
 * The same split as `messages/kinds.ts` and for the same reason (D-5): this package is the
 * CONTRACT. It says that `ack.lending` exists, that it is at version 1, and that rendering it
 * needs an agent, a base, a reach and a count beyond it. It does not say what the sentence is
 * in any language — that is copy, and copy lives in `apps/web/src/i18n/messages/<locale>/`
 * where a translator can find it and where `Messages = typeof en` makes a missing sentence a
 * compile error.
 *
 * ── Why a version, declared here ──
 *
 * "Agreed to `ack.lending`" is not an audit answer on its own, because the sentence behind that
 * key is copy and copy gets improved. The version is what distinguishes *agreed to v1* from
 * *agreed to v2*, and it is bumped whenever the sentence materially changes — a reworded
 * clause that narrows or widens the claim, a fact added or removed. A typo fix is not a
 * material change. The version lives beside the key rather than beside the English sentence so
 * that both catalogues are pinned to the same number by construction; a version per locale
 * would be two records of one agreement.
 *
 * ── Why the wire field is a plain string ──
 *
 * `ackKey` crosses as `z.string()`, not as an enum of `AckKey`. A browser tab that has been
 * open across a deploy may still post the key it rendered, and a pod that has not learned a key
 * must store it rather than refuse the acknowledgement — the rendered text is on the row for
 * exactly that case. Strictness belongs at the two ends: typed at the dialog that renders the
 * sentence, exhaustive in the catalogue that holds it. This is the same reasoning, and the same
 * shape, as `ApiIssue.messageKey`.
 */

// ---------------------------------------------------------------------------
// Values a consent sentence interpolates
// ---------------------------------------------------------------------------

/**
 * Which people a consent sentence is ABOUT, structurally.
 *
 * Never the rendered phrase. "People in Legal" is an English noun phrase with an English
 * preposition in it, and a Portuguese sentence cannot agree with one (D-20) — worse, a record
 * holding it would be a record that can only ever be replayed in the language it was written
 * in, which is the whole defect this shape exists to remove.
 *
 * The three `private` forms are the picker's three ways of naming "only its maintainers"
 * (PRD-26 D-37): the reader themselves, somebody else by name, or a group's managers. They are
 * kept apart because the sentence differs in more than a word — the self form addresses the
 * reader in the second person, and Portuguese conjugates accordingly.
 */
export const AckAudienceSchema = z.object({
  kind: z.enum(['private', 'groups', 'everyone']),
  /** Group names as chosen. Data, never translated (D-17). */
  groups: z.array(z.string()).max(200).default([]),
  privateKind: z.enum(['self', 'person', 'group_managers']).nullable().default(null),
  /** The person's display name or the group's name, for the two non-self private forms. */
  privateName: z.string().nullable().default(null)
})
export type AckAudience = z.infer<typeof AckAudienceSchema>

/**
 * Who a knowledge base fails to reach within an audience (GRP-045), as values.
 *
 * `groups` carries each group's size rather than a pre-sized phrase, because "Legal (8 people)"
 * puts a count, a bracket and a plural noun in one string and Portuguese pluralises zero into
 * the singular category — so the phrase has to be built in the locale that renders it.
 */
export const AckMissingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('everyone'), people: z.number().int().nonnegative() }),
  z.object({
    kind: z.literal('groups'),
    people: z.number().int().nonnegative(),
    groups: z.array(z.object({ name: z.string(), memberCount: z.number().int().nonnegative() })).max(200)
  }),
  z.object({ kind: z.literal('people'), people: z.number().int().nonnegative() })
])
export type AckMissing = z.infer<typeof AckMissingSchema>

// ---------------------------------------------------------------------------
// The keys, their versions, and what each one interpolates
// ---------------------------------------------------------------------------

/**
 * Every consent sentence this product asks somebody to agree to, with the version of the
 * sentence behind it.
 *
 * Four, and there have only ever been four: publishing or sharing an agent, changing who can
 * use a knowledge base, letting an agent answer from a base for people who cannot open it, and
 * starting a connected folder whose contents become searchable by the base's audience.
 */
export const ACKS = {
  publish: { key: 'ack.publish', version: 1 },
  baseAccess: { key: 'ack.base_access', version: 1 },
  lending: { key: 'ack.lending', version: 1 },
  /**
   * Lending, asked for from the builder's Knowledge step rather than from the base's own page.
   *
   * The same decision and the same row, worded differently on purpose: the base's maintainer
   * reading their own page gets five facts about an agent, and a maker who maintains both gets
   * one sentence about what they are about to let their agent do. A key names a SENTENCE, so two
   * sentences cannot share one — a record that could not say which of the two was shown would be
   * a record that cannot be replayed, which is the whole point of having a key.
   */
  lendingFromBuilder: { key: 'ack.lending_from_builder', version: 1 },
  /**
   * RESERVED, and deliberately not wired.
   *
   * `connector_folders` records who acknowledged, when, which access and which groups — and
   * since `0014`, in which language. It has no `ack_text` column and never had one, so there
   * is nothing for a key to sit beside yet. The payload below is declared all the same, so
   * that the day a column is added the shape is already agreed and the four flows stay one
   * shape rather than three plus an exception.
   *
   * It has no call sites on purpose. Anything that reads this list should treat it as the
   * design for a fifth flow, not as a fourth that somebody forgot to finish.
   */
  folderAudience: { key: 'ack.folder_audience', version: 1 }
} as const satisfies Record<string, { key: string; version: number }>

export type AckKey = (typeof ACKS)[keyof typeof ACKS]['key']

/**
 * What each key needs in order to be rendered — the contract between the dialog that collects
 * an acknowledgement and the catalogue that words it.
 *
 * Every member is a VALUE: a count, an id, a name, a boolean, a raw byte figure (D-20). Nothing
 * here is a phrase, an English noun with its article, or a sentence assembled somewhere else,
 * because all three are unrenderable in a language that agrees adjectives with their nouns —
 * and because a parameter that is already prose makes the whole record untranslatable again,
 * one level further down.
 */
export interface AckPayloads {
  /**
   * Publishing an agent, bringing a retired one back, or turning it on for its maintainers.
   *
   * This keys the DISCLOSURE — "what they get" — which is the substantive claim the maker
   * agrees to. What happens now and what an administrator's approval would add are stated in
   * the same dialog and are reconstructible from the audience and the listing's state, which
   * `publish_acks.disclosure` already carries; the stored `ackText` holds all three verbatim.
   */
  'ack.publish': {
    audience: AckAudience
    /**
     * Whether the people the sentence is about are the person reading it.
     *
     * This replaces a `reader: 'they' | 'you'` parameter, which was an English pronoun asked
     * for by name (D-20). Portuguese has no analogue — the distinction lands on the verb, not
     * on a word that can be slotted in — so the parameter now says WHO is being described and
     * each catalogue decides for itself how its language says that.
     */
    subject: 'reader' | 'others'
    /** Attached knowledge bases, by name. Data, never translated. */
    bases: string[]
    /**
     * Attached bases the maker can no longer see. A count, not the phrase that stands in for
     * them: the phrase is copy and is rendered per locale.
     */
    unknownBases: number
    /** Granted capabilities, by name. */
    capabilities: string[]
    /** Granted capabilities that are no longer available, for the same reason as `unknownBases`. */
    unknownCapabilities: number
    /** Bases that will not reach part of the chosen audience, and who they miss. */
    gaps: Array<{ base: string; missing: AckMissing }>
  }
  /**
   * Changing who can use a knowledge base. Two sentences, both the shared audience picker's:
   * who the choice reaches, and whether it waits for an administrator's review.
   *
   * The two members mirror `ReachLine` and `ReviewLine` in the web app's
   * `components/groups/audience-picker.ts`, which is where those sentences' inputs are computed
   * and where the picker's own catalogue entries take them. They are restated here rather than
   * imported because a contract cannot depend on an app — and restating them is what makes the
   * record replayable: the stored parameters are exactly what the picker's entries render from,
   * in whichever locale is asking. The assignment at the dialog is what catches any drift.
   */
  'ack.base_access': {
    base: string
    reach:
      | { kind: 'pending' }
      | { kind: 'no_groups' }
      | { kind: 'private'; people: number | null }
      | { kind: 'everyone'; people: number | null }
      | { kind: 'groups'; people: number | null; delta: number | null }
    review:
      | { kind: 'none' }
      | { kind: 'administrator' }
      | { kind: 'live_now'; managedAdded: string[] }
      | { kind: 'everyone' }
      | { kind: 'groups'; waiting: string[]; nowAdded: string[]; nowRemoved: string[] }
    /**
     * How "only its maintainers" is named for this base (PRD-26 D-37) — the three forms of the
     * picker's private label, as values rather than as the label itself.
     */
    privateOwner: { kind: 'self' | 'person' | 'group_managers'; name: string | null }
  }
  /**
   * Letting an agent answer from a base for everyone it reaches (PRD-26 D-16) — five facts,
   * each one a maintainer has been surprised by.
   */
  'ack.lending': {
    agent: string
    base: string
    /** Who is accountable for the agent: a person by name, or a group whose managers are. */
    ownerKind: 'user' | 'group'
    ownerName: string
    acts: boolean
    delegates: boolean
    /** People the agent reaches today. Meaningless unless it is live, which `live` says. */
    reach: number
    /** How many of them cannot use the base themselves. */
    beyond: number
    live: boolean
  }
  /**
   * The same decision, taken from the builder's Knowledge step by a maker who also maintains the
   * base. One sentence rather than five, and it states fewer facts — so it carries fewer
   * parameters, rather than carrying the others with values nobody read.
   */
  'ack.lending_from_builder': {
    agent: string
    base: string
    ownerKind: 'user' | 'group'
    ownerName: string
    reach: number
    beyond: number
    acts: boolean
  }
  /**
   * Starting a connected folder, whose files become searchable by everyone who can use the base
   * (PRD-25 D-8).
   *
   * `connector_folders` already records this acknowledgement structurally — who, when, which
   * access, which groups — so `0014` gave it only the language it was read in. The payload is
   * declared all the same: it is what the dialog renders from, it keeps the four flows one
   * shape, and it is what a column would hold the day one is added.
   */
  'ack.folder_audience': {
    base: string
    audience: AckAudience
    /** People who can use the base, as the folder's audience line counts them. */
    reach: number
    /** Agents allowed to answer from the base for people who cannot use it (D-16). */
    lentToCount: number
  }
}

/**
 * An acknowledgement's parameters as they cross the wire and land in `ack_params`.
 *
 * Deliberately permissive about SHAPE and strict about MEANING. The payloads above are what a
 * dialog is checked against; this is what a server accepts, and it accepts nested values
 * because a gap carries a list of sized groups and flattening that would lose the association
 * between a group and its count. What it must never carry is a rendered phrase — that is a
 * rule about content, which no schema can enforce and which the payload types above do.
 *
 * The size bound is on the serialized whole rather than on each member, because the shape is
 * open: a per-field cap on an open record bounds nothing. It is generous enough for the largest
 * disclosure this product can compose — every attached base, every capability and a coverage gap
 * per base — and small enough that a body cannot become a way to write arbitrary documents into
 * an audit table.
 */
export const ACK_PARAMS_MAX_BYTES = 32_768

export const AckParamsSchema = z
  .record(z.string().min(1).max(64), z.json())
  .refine((value) => JSON.stringify(value).length <= ACK_PARAMS_MAX_BYTES, {
    message: 'the parameters of an acknowledgement are too large'
  })
export type AckParams = z.infer<typeof AckParamsSchema>

/**
 * The four fields every consent body gains, spread into each request schema.
 *
 * All four are optional, and that is the rolling-deploy contract (ADR-0016): a browser tab
 * loaded before this release posts the body it has always posted, and a pod that predates the
 * release ignores fields it does not know. The rendered text stays REQUIRED where it already
 * was — the evidence is not what became optional here.
 */
export const ackRecordFields = {
  /** Which catalogue sentence was rendered. A plain string on the wire; see the header. */
  ackKey: z.string().min(1).max(80).optional(),
  /** Which version of that sentence. */
  ackVersion: z.number().int().positive().max(10_000).optional(),
  /** The language it was read in. Enum-validated: it selects a template on replay. */
  ackLocale: LocaleSchema.optional(),
  /** The values it interpolated. */
  ackParams: AckParamsSchema.optional()
} as const

/**
 * The four fields as a CLIENT sends them — the mirror of `ackRecordFields`, for the api clients
 * and hooks that forward a body they do not otherwise inspect.
 *
 * Optional rather than nullable, because here absent means "this caller has nothing structured
 * to say", which is a different fact from a stored row that has no key.
 */
export type AckRecordInput = {
  ackKey?: string
  ackVersion?: number
  ackLocale?: Locale
  ackParams?: AckParams
}

/**
 * The same record as the server holds it, with every field nullable.
 *
 * Rows written before `0014` have no key and can never be given one — there is no way to know
 * which version of which sentence was shown — so for them the stored text remains the record.
 * That irreversibility is why D-7 was decided before any catalogue work began, and it is why
 * this type says `null` rather than `undefined`: absent here is a fact about a row, not a
 * field somebody forgot to send.
 */
export type AckRecord = {
  ackKey: string | null
  ackVersion: number | null
  ackLocale: string | null
  ackParams: AckParams | null
}

/** An empty record, for the paths where an old client sent nothing structured. */
export const NO_ACK_RECORD: AckRecord = { ackKey: null, ackVersion: null, ackLocale: null, ackParams: null }

/**
 * Reads the four fields off a parsed request body into the shape the write paths persist.
 *
 * One function rather than four spreads, because the four flows disagreed about nothing here
 * and a fifth consent flow should not have to rediscover the mapping.
 */
export const ackRecordOf = (body: {
  ackKey?: string | undefined
  ackVersion?: number | undefined
  ackLocale?: string | undefined
  ackParams?: AckParams | undefined
}): AckRecord => ({
  ackKey: body.ackKey ?? null,
  ackVersion: body.ackVersion ?? null,
  ackLocale: body.ackLocale ?? null,
  ackParams: body.ackParams ?? null
})

/**
 * The cap on a rendered acknowledgement, raised 30% for Portuguese (PRD-27 LOC-041, F-21).
 *
 * Brazilian Portuguese runs 15–25% longer than the English it is translated from, and three of
 * these four sentences are assembled from four or five clauses each. A cap that truncates an
 * acknowledgement is a cap that decides what somebody agreed to, which is not a decision a
 * validation limit gets to make — so the headroom is deliberate rather than tight.
 */
export const ACK_TEXT_MAX = 5_200

/** The same, for the connected folder's shorter two-clause sentence (`2000` before). */
export const FOLDER_ACK_TEXT_MAX = 2_600
